import React, { useEffect, useRef, useState } from 'react';
import { BaseFileUpload, CombinedRefContent } from '../FileUpload/BaseFileUpload';
import { NestedParam } from '../../types/NestedParam';
import { ImageFileParam } from '../../types/FileUploadParam';
import { HiddenParam } from '../../types/HiddenParam';
import { TextParam } from '../../types/TextParam';
import { CheckBoxParam } from '../../types/CheckBoxParam';
import { RadioParam } from '../../types/RadioParam';
import { formParamsUtils } from '../../utils/formParamsUtils';
import { HiddenInput } from '../HiddenInput/HiddenInput';
import { SelectedImage } from '../../../FileUpload';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import {
  ImagePreview,
  ImageUploadDescription,
  ImageUploadHeadline, ImageUploadMetaData,
  PreviewSuspense,
} from '../../../components/ImageUpload';
import { isValid, useValidateImage } from '../../../FileUpload/utils/validateImage';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useValdidationRules } from '../../../FileUpload/utils/useValdidationRules';
import { LazyImageCropper } from '../../../../components/molecules/ImageCropper/LazyImageCropper';
import { ImageContainer } from '../../../components/ImageUpload/ImageContainer';
import IConstraints from '../../../../types/IContraints';
import { Image } from '../../../../types/api/ImageType';
import { AspectRatioOption } from '../../../../components/molecules/ImageCropper/ImageCropperAspectRatioOptions';
import { ResponsiveType } from '../../../../components/molecules/Image';

// TODO: Add optional types, make more generic?
export type ImageUploadWithCroppingParam = NestedParam<
  'imageuploadparams',
  'imageupload',
  | ImageFileParam<'image'>
  | HiddenParam<'itemId'>
  | HiddenParam<'width'>
  | HiddenParam<'height'>
  | HiddenParam<'y'>
  | HiddenParam<'x'>
  | TextParam<'alt'>
  | TextParam<'copyright'>
  | TextParam<'caption'>
  | CheckBoxParam<'popUp'>
  | RadioParam<'size', 'large' | 'medium' | 'small'>
>;

export type ImageUploadWithCroppingProps = {
  param: ImageUploadWithCroppingParam;
  userSelectableAspectRatios?: AspectRatioOption[] | undefined;
  viewportSwitch?: boolean | undefined;
  cropping?: boolean;
  responsiveType?: ResponsiveType;
};

export function ImageUploadWithCropping({
  param,
  userSelectableAspectRatios,
  viewportSwitch,
  cropping = true,
  responsiveType,
}: ImageUploadWithCroppingProps) {
  const { t } = useSonTranslation('ImageUpload');

  const params = formParamsUtils(param);
  const image = params.getByName('image');
  const itemId = params.findByName('itemId');
  const alt = params.findByName('alt');
  const copyright = params.findByName('copyright');
  const popUp = params.findByName('popUp');
  const caption = params.findByName('caption');
  const size = params.getAllByName('size')
    .sort((element1: any, element2: any) => element1.value.localeCompare(element2.value));

  const [selectedImage, setSelectedImage] = useState<SelectedImage | undefined>(undefined);
  const [backendImage] = useState<typeof image.value | undefined>(image.value);
  const [readingFile, setReadingFile] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(undefined);
  const labelRef = useRef<HTMLLabelElement>(undefined);
  const combinedRef = useRef<CombinedRefContent>({
    inputRef: inputRef,
    labelRef: labelRef,
  });
  const validationRules = useValdidationRules(image.constraints || {});
  const validate = useValidateImage();
  const previewSuspenseId = useHtmlValidId();

  useEffect(() => {
    // gotta reset the customValidity first, otherwise previously set errors will remain on the input element
    inputRef.current?.setCustomValidity('');
    if (selectedImage?.errors && selectedImage.errors.length > 0) {
      inputRef.current?.setCustomValidity(selectedImage.errors.join('\n'));
    }
  }, [selectedImage?.errors]);

  return (
    <>
      <ImageUploadHeadline headlineHierarchy={3} />
      <ImageUploadDescription constraints={image.constraints as IConstraints} />
      <BaseFileUpload
        ref={combinedRef as any} // Typescript misinterprets the CombinedRefContent
        param={image}
        selectButtonLabel={t('ImageUpload:buttons.chooseFile')}
        onSelectedFilesChanged={(event) => {
          const file = event?.target?.files?.item(0);

          if (file) {
            setReadingFile(true);
            validate(file, validationRules)
              .then(value => setSelectedImage(value))
              .catch(() => setSelectedImage(undefined))
              .finally(() => setReadingFile(false));
          } else {
            setReadingFile(false);
            setSelectedImage(undefined);
          }

          labelRef.current?.focus();
        }}
      >
        {selectedImage || backendImage
          ? (
              <PreviewSuspense id={previewSuspenseId} isLoading={readingFile} selectedPreview={selectedImage}>
                {cropping && isValid(selectedImage)
                  ? (
                      <ImageContainer filename={selectedImage.file.name}>
                        <LazyImageCropper
                          src={selectedImage?.image.src}
                          aspectRatio={image.constraints?.aspectRatio}
                          alt={t('previewImageCropper')}
                          minimumCropWidth={image.constraints?.minWidth}
                          viewportSwitch={viewportSwitch}
                          userSelectableAspectRatios={userSelectableAspectRatios}
                        />
                      </ImageContainer>
                    )
                  : (
                      <ImagePreview
                        image={{ ...((isValid(selectedImage) ? selectedImage.image : backendImage) as any),
                          copyright: undefined } as Image}
                        previewFilename={isValid(selectedImage) ? selectedImage.image.fileName : undefined}
                        responsiveType={responsiveType}
                      />
                    )}
              </PreviewSuspense>
            )
          : null}
        {isValid(selectedImage) || backendImage
          ? (
              <ImageUploadMetaData alt={alt} copyright={copyright} size={size} popUp={popUp} caption={caption} />
            )
          : null}
      </BaseFileUpload>
      {itemId ? <HiddenInput {...itemId} /> : null}
    </>
  );
}
